import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, PermissionError, isSuperAdminRateLimitError } from '@/lib/api';
import type { InvoiceCreateRequest, InvoiceUpdate, InvoiceItemCreate } from '@/lib/schemas';
import { toast } from 'sonner';
import { invalidateProjectListQueries } from '@/lib/cache-utils';
import { mutationErrorToast } from '@/lib/globalErrorHandlers';
import { withAddedRecipient, withChangedRecipientRole, withPromotedRecipient, type AddedInvoiceContact } from '@/lib/invoiceRecipientCache';
import { isOutcomeUnknown, takeStartedOver } from '@/lib/invoice-create-operation';

// ============================================================================
// Invoices - Query Hooks
// ============================================================================

export function useInvoices(
  // Phase 9.13b: Added sort_by and sort_order parameters
  params?: { project_id?: string; status?: string; search?: string; sort_by?: string; sort_order?: string; limit?: number; offset?: number },
  options?: { keepPreviousData?: boolean }
) {
  return useQuery({
    queryKey: ['invoices', params],
    queryFn: () => api.invoices.list(params),
    placeholderData: options?.keepPreviousData ? (prev) => prev : undefined,
    // v0.14.4 R5 (stale-tab last-write-wins kill): the global QueryClient default
    // is refetchOnWindowFocus:false. For the invoices LIST specifically we opt back
    // in so a tab that was overwritten by a conflicting write in another tab
    // self-corrects when the user returns to it, instead of displaying a stale
    // status forever. Scoped to this list query only — the global default (and the
    // detail/utility queries) is intentionally left untouched.
    refetchOnWindowFocus: true,
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ['invoices', id],
    queryFn: () => api.invoices.get(id!),
    enabled: !!id,
  });
}

export function useNextInvoiceNumber() {
  return useQuery({
    queryKey: ['invoices', 'next-number'],
    queryFn: () => api.invoices.getNextNumber(),
  });
}

/**
 * R1 (v0.14, W2 kill): Smart-default establishment hint for InvoiceCreatePage.
 *
 * Refetches whenever `client_scope_id` or `organization_id` changes so the
 * picker re-derives its decision-tree input when the user picks a different
 * client scope on the create form.
 *
 * staleTime 5min — same-session re-renders skip the network. Invoice-create
 * sessions are short-lived; longer staleness would defeat the point.
 *
 * Graceful-degrade: on error the hook returns `data: null` (not undefined,
 * and no error toast) so the caller can fall through to the no-hint branch
 * of its decision tree. This mirrors the backend's 200-on-error contract.
 */
export function useInvoiceEstablishmentHint(args: {
  projectId: string | undefined;
  clientScopeId?: string | undefined;
  organizationId?: string | undefined;
}) {
  const { projectId, clientScopeId, organizationId } = args;
  return useQuery({
    queryKey: ['invoice-establishment-hint', projectId, clientScopeId, organizationId],
    queryFn: async () => {
      if (!projectId) return null;
      try {
        return await api.projects.getInvoiceEstablishmentHint(projectId, {
          organization_id: organizationId,
          client_scope_id: clientScopeId,
        });
      } catch (err) {
        // Never throw — picker must be able to render without a hint.
        // Matches the backend's graceful-degrade contract (always 200,
        // null payload on error).
        return null;
      }
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Phase 10a.4: Billing queue for billing session flow.
 * Returns active projects with their unbilled billable items.
 * staleTime: 0 ensures fresh data every time the session starts.
 */
export function useBillingQueue() {
  return useQuery({
    queryKey: ['invoices', 'billing-queue'],
    queryFn: () => api.invoices.getBillingQueue(),
    staleTime: 0,
  });
}

// ============================================================================
// Invoices - Mutation Hooks
// ============================================================================

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InvoiceCreateRequest) => api.invoices.create(data),
    onSuccess: (response, variables) => {
      // Invalidate all related caches (moved from InvoiceCreatePage component)
      // Using invalidateQueries (not refetch) - data refetches when detail page mounts
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', 'next-number'] });
      invalidateProjectListQueries(queryClient);
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'billable-items'
      });

      if (response.replayed) {
        // R4: a retry, a reload or a second tab found the invoice an earlier press already made. Say so in
        // plain words; she is about to land on it, not on a new one.
        toast.success('Your invoice was already saved. Opening it.');
        return response.invoice;
      }

      if (response.identity_resolution_failed) {
        // The invoice exists, so this is not an error — but it was created
        // without the DBA, recipients and billing address it should have
        // inherited, and "created successfully" over that is how she ends up
        // sending an invoice addressed to nobody.
        // Deliberately does NOT enumerate "the DBA, recipients and address".
        // Two different failures set this flag: the resolver throwing (all
        // three missing) and the recipient write failing (only recipients --
        // the DBA and address were already applied). Naming all three on the
        // second case sends her to re-verify two fields that are correct, which
        // is a tax on the person with a ten-invoice backlog.
        toast.warning(
          `Invoice ${response.invoice?.invoice_number || 'was created'}` +
            `${response.invoice?.invoice_number ? ' was created' : ''}, but some ` +
            `of its billing details could not be filled in automatically. ` +
            `Check the Bill To panel before sending.`,
          { duration: Infinity },
        );
      } else {
        toast.success('Invoice created successfully');
      }
      return response.invoice;
    },
    onError: (error: any, variables) => {
      // R4: a keyed create whose answer never arrived (no response, 408, 429, 502, 503, 504) is NOT shown as an
      // error. The page shows "Checking whether your invoice was created" instead, because an error toast invites
      // a second press and a second invoice. Everything else is an answer from our API and means nothing was
      // saved, so it IS shown: a refusal (4xx) in its own words. A keyed 500 is shown by the page after its one same-key replay.
      if (variables?.operation_key && isOutcomeUnknown(error)) return;
      if (error.status === 409 && error.data?.detail?.code === 'invoice_create_operation_conflict') {
        toast.error('This invoice was already started with different details. Open the invoice list to check it.');
      } else if (error.status === 409) {
        // R5: right after "It is not there, start over", the first press may well have made this invoice.
        if (takeStartedOver(variables?.project_id as string | undefined)) {
          const number = variables?.invoice_number ? `Invoice ${variables.invoice_number}` : 'That invoice number';
          toast.error(`${number} already exists. It may be the one you just made. Open the Invoices list to check before you change the number.`);
        } else {
          toast.error('Invoice number already exists');
        }
      } else if (variables?.operation_key && typeof error?.status === 'number' && error.status >= 500) {
        // M5: silent on purpose. A keyed 500 can follow a commit, so the page replays it once with the same key and
        // only then shows the plain error itself (InvoiceCreatePage). An error here would show first, then be wrong.
        return;
      } else {
        toast.error(error.message || 'Failed to create invoice');
      }
    },
  });
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: InvoiceUpdate }) =>
      api.invoices.update(id, data),
    // R8f Finding #12 (v0.14 org-change fixwave 2026-05-28):
    // never retry on 4xx client errors. 409 (duplicate number) and 412
    // (organization_mismatch / establishment_mismatch race) are intentional
    // backend responses, not transient failures — retrying produces a
    // duplicate PUT in the network panel and confuses race UX.
    retry: (_failureCount, error: any) => {
      const status = error?.status;
      if (status === 409 || status === 412 || status === 403 || status === 422) return false;
      // 2026-05-30 audit: the QueryClient global default for mutations is now 0
      // (was 1 — a cargo-cult copy of queries.retry that silently risked duplicate
      // payments, duplicate proposal emails, etc. on a 5xx-after-success). This
      // hook KEEPS one explicit retry for non-4xx transients because
      // useUpdateInvoice has historically been idempotent at the API layer
      // (server enforces invoice-number uniqueness, optimistic concurrency on
      // org-change) and the UX benefit of self-healing a single hiccup
      // outweighs the duplicate-PUT risk for this specific endpoint.
      return _failureCount < 1;
    },
    onSuccess: async (response, variables) => {
      // CRITICAL: await to ensure refetch completes before UI continues
      // Fixes race condition where status badge shows old value after update
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      invalidateProjectListQueries(queryClient);
      toast.success('Invoice updated successfully');
      return response.invoice;
    },
    onError: (error: any) => {
      if (error.status === 409) {
        toast.error('Invoice number already in use');
      } else if (error.status === 412) {
        // 412 race is handled per-caller (e.g., OrgChangeStepper renders its
        // own toast). Skip the generic "Failed to update invoice" so we don't
        // double-up.
        return;
      } else if (error.status === 403) {
        // 2026-08-06: same reasoning as the 412 skip above. The ONLY consumer of
        // this hook is OrgChangeStepper (:133), which renders its own, more
        // specific toast for invoice_locked and falls back to a catch-all for a
        // genuine denial — so nothing is swallowed today.
        //
        // 2026-08-07 correction: this comment previously also cited "BillToPanel's
        // inline banner". BillToPanel does not use this hook at all. The skip was
        // safe anyway, but the stated reason was never true, which is exactly how
        // a second caller would inherit a SILENT 403 on the strength of it.
        // If you add a consumer, it MUST render its own 403 message or this skip
        // has to go and the de-duplication move to the caller.
        //
        // Without this, React Query fires BOTH this hook-level
        // onError and the per-call one — query-core runs mutationCache.onError,
        // then options.onError, then mutateOptions.onError — so she got two
        // toasts for one failed click, and with duration: Infinity elsewhere in
        // this file that means two things to dismiss by hand.
        return;
      } else {
        toast.error(error.message || 'Failed to update invoice');
      }
    },
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.invoices.delete(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['invoices'] });
      const previousInvoices = queryClient.getQueriesData({ queryKey: ['invoices'] });

      try {
        // Update each cached list query individually via setQueryData (exact key).
        // setQueriesData with a prefix match has proven unreliable — iterating
        // manually with the snapshot keys is more predictable.
        for (const [queryKey, data] of previousInvoices) {
          if (data && Array.isArray((data as any).invoices)) {
            queryClient.setQueryData(queryKey, {
              ...(data as any),
              invoices: (data as any).invoices.filter((inv: any) => inv.id !== id),
              total: ((data as any).total ?? (data as any).invoices.length) - 1,
            });
          }
        }
        queryClient.removeQueries({ queryKey: ['invoices', id] });
      } catch {
        // Optimistic update failed — the actual delete will still proceed
      }

      return { previousInvoices };
    },
    onError: (error: any, id, context) => {
      // Rollback on error
      if (context?.previousInvoices) {
        context.previousInvoices.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      // Show the server error message if available, with sensible fallbacks
      const message = error.message || (error.status === 400
        ? 'Cannot delete this invoice (it may be sent or paid)'
        : 'Failed to delete invoice');
      toast.error(message);
    },
    onSuccess: async (_, id) => {
      // CRITICAL: Cancel any in-flight refetches FIRST.
      // When the confirmation dialog closes, refetchOnWindowFocus fires a list
      // refetch while the DELETE is still in flight. That stale response can
      // arrive AFTER this callback and overwrite the cache. Cancelling here
      // ensures it never lands.
      await queryClient.cancelQueries({ queryKey: ['invoices'] });

      // Re-apply optimistic removal (belt-and-suspenders after cancel)
      const allQueries = queryClient.getQueriesData({ queryKey: ['invoices'] });
      for (const [queryKey, data] of allQueries) {
        if (data && Array.isArray((data as any).invoices)) {
          const invoices = (data as any).invoices;
          if (invoices.some((inv: any) => inv.id === id)) {
            queryClient.setQueryData(queryKey, {
              ...(data as any),
              invoices: invoices.filter((inv: any) => inv.id !== id),
              total: ((data as any).total ?? invoices.length) - 1,
            });
          }
        }
      }
      queryClient.removeQueries({ queryKey: ['invoices', id] });

      // Do NOT invalidate ['invoices'] here — the optimistic removal above is
      // the source of truth. An immediate refetch can hit the DB before the
      // DELETE has fully propagated (Cloud SQL read-after-write lag), returning
      // stale data that re-adds the row. The list refreshes naturally on next
      // navigation or window focus.
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', 'next-number'] });
      invalidateProjectListQueries(queryClient);
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'billable-items'
      });
      toast.success('Invoice deleted successfully');
    },
  });
}

/**
 * Cancel-invoice feature: POST /{id}/cancel. Unlike useDeleteInvoice, there
 * is no optimistic removal — the invoice stays in the list, just with a new
 * status — so this hook lets the real server response drive the cache.
 *
 * Invalidates broadly on success because cancelling has real financial side
 * effects the caller can't predict client-side: the invoice's OWN lines and
 * total are UNCHANGED (data-loss prevention -- cancel never deletes or
 * recomputes them), but its billable items read as unbilled again (every
 * "is this billed" aggregation excludes cancelled invoices) and it drops out
 * of every outstanding/available-to-invoice figure -- so the invoice
 * detail/list, billable items, the owning project's financials, and
 * dashboard stats all need a refetch.
 */
export function useCancelInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
      confirmDestructiveEntityId,
    }: {
      id: string;
      data: { reason?: string };
      confirmDestructiveEntityId?: string;
    }) => api.invoices.cancel(id, data, confirmDestructiveEntityId),
    onSuccess: async (response, variables) => {
      const projectId = response.invoice?.project_id;

      await queryClient.invalidateQueries({ queryKey: ['invoices', variables.id] });
      await queryClient.invalidateQueries({ queryKey: ['invoices', variables.id, 'full'] });
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'billable-items',
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'dashboard',
      });
      invalidateProjectListQueries(queryClient);
      if (projectId) {
        // The project DETAIL query carries the financials (paid_fee /
        // unpaid_fee) that a cancel changes — invalidateProjectListQueries
        // deliberately skips detail caches (see cache-utils.ts), so this
        // targeted invalidation is the one that actually refreshes them.
        queryClient.invalidateQueries({ queryKey: ['projects', 'detail', projectId] });
      }

      toast.success('Invoice cancelled');
    },
    onError: (error: any) => {
      if (isSuperAdminRateLimitError(error)) {
        toast.error('Too many destructive actions — wait a minute and retry.', { duration: Infinity });
        return;
      }
      mutationErrorToast(error, 'Failed to cancel invoice');
    },
  });
}

/**
 * SPEC-invoice-merge-2026-08-17 R3: combine two invoices into one.
 *
 * No optimistic update here (unlike useDeleteInvoice) -- a merge changes
 * BOTH invoices (survivor's items/total, absorbed's status/pointer/notes)
 * in ways this hook cannot predict client-side, so the caller
 * (InvoiceListPage) invalidates and lets the real, authoritative rows come
 * back from the server (AC8: the retired invoice must still show up, in
 * whatever state R1 specified, not vanish).
 *
 * Deliberately no toast/dialog-closing here either: the caller passes its
 * own onSuccess/onError to `.mutate()` so it can keep MergeConfirmationDialog
 * open and show R1's specific refusal reason inline on failure (AC7, AC9)
 * rather than have this hook swallow it into a generic toast.
 */
export function useMergeInvoices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { invoice_id_a: string; invoice_id_b: string }) =>
      api.invoices.merge(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
    },
  });
}

// ============================================================================
// Per-invoice contact CRUD — recipients managed directly on the invoice page
// ============================================================================

const _invalidateInvoiceCaches = (queryClient: ReturnType<typeof useQueryClient>, invoiceId: string) => {
  queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId, 'full'] });
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (key[0] !== 'invoices' || key.length < 2) return false;
      const second = key[1];
      if (typeof second === 'string' && second.includes('-')) return false;
      if (second === 'next-number') return false;
      return true;
    },
  });
};

export function useEligibleInvoiceContacts(invoiceId: string | undefined) {
  return useQuery({
    queryKey: ['invoices', invoiceId, 'eligible-contacts'],
    queryFn: () => api.invoices.getEligibleContacts(invoiceId!),
    enabled: !!invoiceId,
  });
}

export function useAddInvoiceContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      data,
    }: {
      invoiceId: string;
      // R3a (v0.14): pass-through type for field_update_mode so the BILL TO
      // panel can request DBA/org propagation in a single POST. Per
      // feedback_mutation_hook_field_drift.md: do NOT explicit-pick fields
      // here — let the data object pass through whole so new optional fields
      // don't silently disappear from the network request.
      data: {
        contact_id?: string;
        contact_name?: string;
        contact_email?: string;
        // R1 (hotfix v0.14.5, AC8): optional — omit to let the backend
        // auto-assign first-recipient-becomes-TO.
        role?: 'to' | 'cc';
        field_update_mode?: 'invoice_only' | 'save_dba_default' | 'save_org_default' | 'save_project_default';
        // 'save_project_default' only: saved people on this invoice to keep for the project with the new person.
        project_set_contact_ids?: string[];
        // 'save_project_default' only (R2 AC14): typed-in rows to save for the project in the same request.
        project_set_row_ids?: string[];
      };
    }) => api.invoices.addContact(invoiceId, data),
    onSuccess: (resp, { invoiceId, data }) => {
      // SPEC-christa-contacts-identity R1 AC18 (finding o): show the person the server just confirmed on the
      // invoice page AT ONCE, from its own answer, then let the re-read below reconcile. The Bill To list and
      // the "Send this invoice to" card both render from this one cached read; waiting for the whole re-read
      // (many queries on the server) made a slow answer look like "nothing happened".
      const saved = (resp as { invoice_contact?: AddedInvoiceContact } | undefined)?.invoice_contact;
      if (saved) {
        queryClient.setQueryData(['invoices', invoiceId, 'full'], (old: Record<string, unknown> | undefined) =>
          withAddedRecipient(old, saved));
      }
      _invalidateInvoiceCaches(queryClient, invoiceId);
      // Hotfix2 — Bug #4: when the caller asks to propagate
      // (save_dba_default / save_org_default) and the backend can't
      // actually do it (e.g. invoice has no establishment for
      // save_dba_default), R3a returns propagation_result.applied=false
      // with a human reason. The old toast just said "Recipient added"
      // and the user thought the default had been saved — silent failure.
      // Now we surface the reason so they know to set the DBA first / pick
      // a different mode.
      const propRequested =
        data.field_update_mode === 'save_dba_default' ||
        data.field_update_mode === 'save_org_default';
      const propResult: any = (resp as any)?.propagation_result;
      if (propRequested && propResult && propResult.applied === false) {
        const reason =
          propResult.reason ||
          (data.field_update_mode === 'save_dba_default'
            ? "invoice has no DBA set"
            : "the contact couldn't be saved as a default");
        toast.warning(`Recipient added — but couldn't save as default: ${reason}.`);
        return;
      }
      toast.success('Recipient added');
    },
    onError: (err: any) => {
      if (err instanceof PermissionError || err?.name === 'PermissionError' || err?.status === 403) {
        // 2026-08-07: prefer the SERVER's sentence when it sent one. This branch
        // used to discard err.message unconditionally, so a status-based 403
        // (e.g. an invoice locked by a payment) was reported as a permissions
        // problem — the same conflation that made Christa read a payment lock on
        // Marine Drive as "permission denied" (2026-08-04). Now that throwIf403
        // reads the real `{error: {...}}` envelope, err.message carries the
        // backend's actual reason whenever there is one.
        //
        // The literal fallback still wins over the generic string: when the
        // server sends no message at all, extractErrorMessage yields the bare
        // "Permission denied", which tells her nothing about what to do next.
        const serverReason =
          err?.message && err.message !== 'Permission denied' ? err.message : null;
        toast.error(
          serverReason || "You don't have permission to add recipients to this invoice.",
          { duration: Infinity },
        );
        return;
      }
      mutationErrorToast(err, 'Failed to add recipient');
    },
  });
}

export function useUpdateInvoiceContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      rowId,
      data,
    }: {
      invoiceId: string;
      rowId: string;
      data: { contact_name?: string; contact_email?: string; role?: 'to' | 'cc'; also_project_default?: boolean };
    }) => api.invoices.updateContact(invoiceId, rowId, data),
    onSuccess: (resp, { invoiceId, rowId }) => {
      // R2 AC13: show the role the server just confirmed in every list at once, then let the re-read reconcile.
      const saved = (resp as { invoice_contact?: { role?: string } } | undefined)?.invoice_contact;
      if (saved?.role) {
        queryClient.setQueryData(['invoices', invoiceId, 'full'], (old: Record<string, unknown> | undefined) =>
          withChangedRecipientRole(old, rowId, saved.role as string));
      }
      _invalidateInvoiceCaches(queryClient, invoiceId);
      // R2 AC13: a project role change must reach the pages that read the project's saved people.
      queryClient.invalidateQueries({ queryKey: ['project-recipient-sets'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-recipient-preview'] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to update recipient');
    },
  });
}

export function useRemoveInvoiceContact() {
  const queryClient = useQueryClient();
  return useMutation({
    // R3a (v0.14): fieldUpdateMode flows through as a query string on the
    // DELETE — controls whether the org_contact's is_accounting_contact flag
    // is flipped OFF alongside the invoice_contacts row removal.
    mutationFn: ({
      invoiceId,
      rowId,
      fieldUpdateMode,
    }: {
      invoiceId: string;
      rowId: string;
      fieldUpdateMode?: 'invoice_only' | 'stop_for_dba' | 'stop_for_org';
    }) => api.invoices.removeContact(invoiceId, rowId, fieldUpdateMode),
    onSuccess: (_resp, { invoiceId }) => {
      _invalidateInvoiceCaches(queryClient, invoiceId);
      toast.success('Recipient removed');
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to remove recipient');
    },
  });
}

// R3a (v0.14): per-recipient star toggle. POST to
// /api/invoices/{id}/contacts/{contact_id}/toggle-default with toggle_to +
// scope so the org_contact's is_accounting_contact flag flips in one step.
// Drives the ★/☆ button on each BILL TO recipient row.
export function useToggleRecipientDefault() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      contactId,
      data,
    }: {
      invoiceId: string;
      contactId: string;
      data: { toggle_to: boolean; scope: 'dba' | 'org' };
    }) => api.invoices.toggleRecipientDefault(invoiceId, contactId, data),
    onSuccess: (_resp, { invoiceId }) => {
      _invalidateInvoiceCaches(queryClient, invoiceId);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Failed to update recipient default');
    },
  });
}

// R3 (SPEC-invoice-send-safety-2026-08-17): promote a `typed_in` recipient
// (invoice_contacts.contact_id IS NULL) to a saved contact on the invoice's
// client. POST /api/invoices/{id}/contacts/{rowId}/promote, no body — the
// backend decides establishment scope (AC6) and whether to reuse an existing
// contact by email or create a new one (org_contact_created in the response
// tells the caller which happened). Drives the "Save to client" affordance
// next to a one-off recipient row.
export function usePromoteInvoiceContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, rowId, body }: {
      invoiceId: string;
      rowId: string;
      // R2 AC14: pass `{ target: 'project', ... }` to save the person for the project instead of the client.
      body?: { target: 'project'; project_set_contact_ids?: string[]; project_set_row_ids?: string[] };
    }) => api.invoices.promoteContact(invoiceId, rowId, body),
    onSuccess: (resp: any, { invoiceId, rowId, body }) => {
      // R2 AC14: the person is a saved contact in the same render; the re-read below reconciles the rest.
      const promoted = resp?.invoice_contact as { contact_id?: string | null } | undefined;
      if (promoted?.contact_id) {
        queryClient.setQueryData(['invoices', invoiceId, 'full'], (old: Record<string, unknown> | undefined) =>
          withPromotedRecipient(old, rowId, promoted.contact_id as string, body?.target === 'project'));
      }
      _invalidateInvoiceCaches(queryClient, invoiceId);
      if (body?.target === 'project') {
        queryClient.invalidateQueries({ queryKey: ['project-recipient-sets'] });
        queryClient.invalidateQueries({ queryKey: ['invoice-recipient-preview'] });
        toast.success('Saved for this project');
        return;
      }
      if (resp?.org_contact_created) {
        toast.success('Saved as a new contact on this client');
      } else {
        toast.success('Linked to an existing contact on this client');
      }
    },
    onError: (err: any) => {
      mutationErrorToast(err, 'Failed to save recipient to client');
    },
  });
}

/**
 * Re-run the accounting-contacts snapshot for an invoice.
 *
 * Use after a contact is added/edited via the inline Bill To CTA so the
 * recipient pills refresh immediately without the user having to reload.
 */
export function useRefreshInvoiceContacts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoiceId: string) => api.invoices.refreshContacts(invoiceId),
    onSuccess: (_response, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId, 'full'] });
      // List rows carry the recipient column too
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (key[0] !== 'invoices' || key.length < 2) return false;
          const second = key[1];
          if (typeof second === 'string' && second.includes('-')) return false;
          if (second === 'next-number') return false;
          return true;
        },
      });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to refresh recipients');
    },
  });
}

// ============================================================================
// Invoice Items - Query Hooks
// ============================================================================

export function useInvoiceItems(invoiceId: string | undefined) {
  return useQuery({
    queryKey: ['invoice-items', invoiceId],
    queryFn: () => api.invoiceItems.list(invoiceId!),
    enabled: !!invoiceId,
  });
}

// ============================================================================
// Invoice Items - Mutation Hooks
// ============================================================================

export function useCreateInvoiceItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InvoiceItemCreate) => api.invoiceItems.create(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'billable-items'
      });
      invalidateProjectListQueries(queryClient);
      toast.success('Invoice item added successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add invoice item');
    },
  });
}

export function useDeleteInvoiceItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.invoiceItems.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'billable-items'
      });
      invalidateProjectListQueries(queryClient);
      toast.success('Invoice item removed successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove invoice item');
    },
  });
}

/**
 * Phase 9.27: Update invoice item (amount/tax changes per-invoice)
 *
 * Used for per-invoice amount edits. Changes only affect this invoice,
 * not other invoices that include the same billable item.
 *
 * For description edits that should cascade to all invoices,
 * use useUpdateBillableItem instead.
 */
export function useUpdateInvoiceItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: {
      id: string;
      data: {
        amount?: number;
        tax_rate?: number;
        // v0.14.4 R2: per-line tax editing sends the rounded tax_amount alongside
        // tax_rate so the line matches the backend's own 2dp derivation.
        tax_amount?: number;
        description?: string;
        // Phase 9.34: quantity/rate fields the amount editor already sends.
        quantity?: number | null;
        unit_price?: number | null;
        unit_type?: 'hours' | 'days' | 'units' | 'each' | null;
        // Christa 2026-09-25: also lower the billable item total to what is billed (server-computed, never below
        // what other invoices bill).
        lower_billable_item_total?: boolean;
      };
    }) =>
      api.invoiceItems.update(id, data),
    onSuccess: (_, variables) => {
      // Invalidate related caches — but EXCLUDE ['invoices', id, 'full'] detail queries.
      // InvoiceItemRow uses setQueryData to update the detail cache directly from the
      // mutation response (avoids Cloud Run stale-read flash). A broad ['invoices']
      // invalidation here would trigger a background refetch that races with and
      // overwrites that optimistic update, causing a brief "No items yet" flash.
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      queryClient.invalidateQueries({
        queryKey: ['invoices'],
        predicate: (query) => !(query.queryKey.length === 3 && query.queryKey[2] === 'full'),
      });
      // Billable items may show updated "invoiced amount" totals
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'billable-items'
      });
      invalidateProjectListQueries(queryClient);
      toast.success('Invoice item updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update invoice item');
    },
  });
}

// ============================================================================
// Phase 6 Batch 5.3: Inline Editing with Optimistic Updates
// ============================================================================

/**
 * Inline invoice editing with optimistic updates
 *
 * Pattern 3: True Optimistic Updates
 * Reference: docs/patterns/frontend/REACT_QUERY_PATTERNS.md
 * Example: src/hooks/useProjects.ts (useUpdateProjectInline)
 *
 * Use for: Inline editing on InvoiceListPage + InvoiceDetailPage
 * - Status dropdown (draft, ready, sent, paid, cancelled)
 * - Internal notes (textarea)
 * - Payment date/amount (when marking as paid)
 */
export function useUpdateInvoiceInline() {
  const queryClient = useQueryClient();

  return useMutation({
    // Mutation key for tracking concurrent mutations
    mutationKey: ['updateInvoiceInline'],

    // `confirmDestructiveEntityId` (super-admin destructive-op guard): when the
    // caller is a super-admin confirming a guarded status downgrade, it passes
    // the invoice id here so the PUT carries the X-Super-Admin-Confirm-Destructive
    // header. Undefined for everyone else (and for non-downgrade changes).
    mutationFn: ({ id, data, confirmDestructiveEntityId }: { id: string; data: Partial<InvoiceUpdate>; confirmDestructiveEntityId?: string }) =>
      api.invoices.update(id, data, confirmDestructiveEntityId),

    // PATTERN 3: True Optimistic Update (update BEFORE server confirms)
    onMutate: async ({ id, data }) => {
      // CRITICAL: Do optimistic update FIRST (synchronously) before any await
      // This ensures the UI updates immediately when state changes

      // Snapshot the previous values for rollback (both old and new query key formats)
      const previousSimple = queryClient.getQueryData(['invoices', id]);
      const previousFull = queryClient.getQueryData(['invoices', id, 'full']);
      // v0.14.4 R5 (silent-loss kill): snapshot EVERY list query too, not just
      // the detail caches. Mirrors useDeleteInvoice's getQueriesData pattern.
      // The prior code only rolled back the detail caches in onError, so when a
      // status PUT was rejected (e.g. 400 "no line items") the list row badge
      // kept the rejected value indefinitely — the deterministic silent-loss
      // reproduced on the invoice list page. Capturing the list snapshots here
      // lets onError restore the rows to their pre-mutation state.
      const previousLists = queryClient.getQueriesData({ queryKey: ['invoices'] });

      // 1. Optimistically update DETAIL query caches (synchronous)
      // Phase 9.19e: Support both old ['invoices', id] and new ['invoices', id, 'full'] query keys
      queryClient.setQueryData(['invoices', id], (old: any) => {
        if (!old) return old;
        // Cache stores invoice directly, not nested in { invoice: {...} }
        return { ...old, ...data };
      });

      // 1b. Update the combined query cache (Phase 9.19e format)
      queryClient.setQueryData(['invoices', id, 'full'], (old: any) => {
        if (!old) return old;
        // Combined query stores data as { invoice: {...}, invoice_items: [...], ... }
        return { ...old, invoice: { ...old.invoice, ...data } };
      });

      // 2. Optimistically update LIST query caches (synchronous)
      // Find all list queries and update the invoice within their arrays
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey;
            // Match list queries: ['invoices', statusFilter, search]
            // Exclude detail queries (UUIDs with hyphens) and utility queries
            if (key[0] !== 'invoices' || key.length < 2) return false;
            const secondKey = key[1];
            if (typeof secondKey === 'string' && secondKey.includes('-')) return false;
            if (secondKey === 'next-number') return false;
            return true;
          }
        },
        (old: any) => {
          if (!old?.invoices) return old;
          // Update the invoice in the list
          return {
            ...old,
            invoices: old.invoices.map((invoice: any) =>
              invoice.id === id ? { ...invoice, ...data } : invoice
            )
          };
        }
      );

      // Cancel any outgoing refetches AFTER optimistic update (async is fine here)
      await queryClient.cancelQueries({ queryKey: ['invoices', id] });
      await queryClient.cancelQueries({ queryKey: ['invoices', id, 'full'] });

      // Return context for rollback (both cache key formats + list snapshots)
      return { previousSimple, previousFull, previousLists, id };
    },

    // Rollback on error
    onError: (err: any, variables, context) => {
      // Rollback both cache key formats
      if (context?.previousSimple) {
        queryClient.setQueryData(
          ['invoices', context.id],
          context.previousSimple
        );
      }
      if (context?.previousFull) {
        queryClient.setQueryData(
          ['invoices', context.id, 'full'],
          context.previousFull
        );
      }
      // v0.14.4 R5: restore EVERY list query from its pre-mutation snapshot so
      // the rejected status never lingers on the list-page badge. Without this
      // the optimistic setQueriesData in onMutate is never undone on the list,
      // and the row keeps showing e.g. "Sent" after the server rejected it.
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      // Super-admin destructive-op rate limit (429): map to a clear cooldown
      // message instead of the raw backend detail, so a super-admin who trips
      // the 10-ops/60s limiter knows to wait rather than seeing a generic error.
      if (isSuperAdminRateLimitError(err)) {
        toast.error('Too many destructive actions — wait a minute and retry.', { duration: Infinity });
        return;
      }
      // Show the actual error message from the server (e.g., "Cannot mark invoice
      // as ready or sent with no line items"). v0.14.4 R5 made this persistent
      // (duration: Infinity + closeButton from the global <Toaster>) so a
      // rejected status change can't flash and vanish before the user reads it.
      //
      // 2026-08-06: routed through mutationErrorToast so persistence applies to
      // 4xx ONLY. Persisting unconditionally meant a network blip or a 500 left
      // a hand-dismiss toast for something that fixed itself — and under
      // `networkMode: 'always'` those now reach this handler instead of being
      // parked, so an offline user could stack one per click reading "Failed to
      // fetch".
      mutationErrorToast(err, 'Failed to update invoice');
    },

    // Show success toast
    onSuccess: (response: any) => {
      toast.success('Invoice updated');
      // v0.12.22 Fix 1: when the backend signals it back-filled the project
      // scope's default establishment from a previously-NULL state, surface
      // that side-effect to the user so they understand why future invoices
      // on this project will pre-fill the same client.
      if (response?.scope_updated) {
        toast.success('Saved as the default client for this project.');
      }
    },

    // Sync with server AFTER mutation is fully settled
    onSettled: (_, __, variables) => {
      // CRITICAL FIX: Only invalidate if no OTHER inline mutations are pending.
      // This prevents race conditions where first mutation's refetch overwrites
      // second mutation's optimistic update with stale server data.
      //
      // v0.14.4 R5 (silent-loss kill): React Query still counts THIS mutation in
      // isMutating() while onSettled runs — it's only removed from the count
      // afterward. The old guard `=== 0` therefore evaluated to false even for a
      // lone status change, so the post-settle invalidation block NEVER ran for a
      // single inline edit — the list never refetched after settle (compounding
      // the broken exact:true bug). The correct test is "<= 1": allow when this
      // settling mutation is the only one in flight; suppress only when ANOTHER
      // updateInvoiceInline is genuinely still pending.
      const inlineMutationsInProgress = queryClient.isMutating({
        mutationKey: ['updateInvoiceInline']
      });

      if (inlineMutationsInProgress <= 1) { // <= 1 = this settling mutation is the only one
        // v0.14.4 R5 (silent-loss kill): the previous line was
        //   invalidateQueries({ queryKey: ['invoices'], exact: true })
        // which ONLY matches a bare ['invoices'] key. The real list key is
        // ['invoices', params] (length 2, e.g. ['invoices', { status, search, ... }]),
        // so exact:true never matched it and the list NEVER refetched after settle —
        // not on success, not on failure. That left the list silently out of sync
        // with the server (the core of the staging repro). The predicate
        // invalidation below already covers every real list query, so the broken
        // exact:true call is removed rather than replaced.

        // When establishment_id changes, the backend re-snapshots invoice_contacts.
        // The optimistic update only patches `invoice.*` — the snapshot array stays stale
        // until refetched. Invalidate the full detail query so TO/CC reflect the new
        // establishment's accounting contacts immediately.
        //
        // V175 (bill-to fix 2026-05): suppress_attention_line lives on invoice.* but the
        // BILL TO warning/note reads from to_contacts_meta, a sibling field that the
        // optimistic update doesn't touch. Without invalidating the full query, the
        // Suppress and Restore buttons succeed at the API layer but the UI keeps showing
        // the prior state until manual reload. Same root cause as the establishment_id
        // case above — derived fields outside invoice.* go stale.
        const data = variables.data ?? {};
        if ('establishment_id' in data || 'suppress_attention_line' in data) {
          queryClient.invalidateQueries({ queryKey: ['invoices', variables.id, 'full'] });
        }

        // Invalidate filtered lists: ['invoices', statusFilter, search]
        // But exclude detail queries: ['invoices', id] and utility queries: ['invoices', 'next-number']
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            // Match ['invoices', statusFilter, search] where:
            // - statusFilter is string | undefined
            // - search is string
            // Exclude detail queries where key[1] is a UUID (contains hyphens)
            // Exclude utility queries like ['invoices', 'next-number']
            if (key[0] !== 'invoices' || key.length < 2) return false;

            const secondKey = key[1];
            // If second key is a string containing hyphens, it's likely a UUID (detail query)
            if (typeof secondKey === 'string' && secondKey.includes('-')) return false;
            // If second key is 'next-number', it's a utility query
            if (secondKey === 'next-number') return false;

            // Otherwise it's a list query with filters
            return true;
          }
        });

        // Invalidate detail query to pick up any item changes whose refetches
        // were cancelled by onMutate's cancelQueries. The optimistic update only
        // covers invoice-level fields (status, notes, etc.) — item additions,
        // edits, or deletions that triggered a refetch before this inline mutation
        // would have had that refetch cancelled, leaving stale invoice_items and
        // stale server-computed totals (total_amount, fee_amount, tax_amount).
        // This refetch runs AFTER optimistic update is applied and the mutation
        // is settled, so it won't interfere with in-flight optimistic updates.
        if (variables?.id) {
          queryClient.invalidateQueries({ queryKey: ['invoices', variables.id, 'full'] });
        }

        // Also invalidate project list queries that might show invoice status
        // Exclude detail queries to prevent stale reads overwriting fresh cache
        invalidateProjectListQueries(queryClient);
      }
    },
  });
}

// ============================================================================
// Batch Invoice Date Update Hook
// ============================================================================

/**
 * Batch update invoice_date for selected invoices with optimistic updates.
 *
 * Pattern 3: True Optimistic Updates
 * Sets invoice_date in the cache immediately, rolls back on error.
 */
export function useBatchUpdateInvoiceDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { invoice_ids: string[]; invoice_date: string }) =>
      api.invoices.batchUpdateDate(data),

    onMutate: async ({ invoice_ids, invoice_date }) => {
      // Snapshot all list query caches for rollback
      const previousData = queryClient.getQueriesData({ queryKey: ['invoices'] });

      // Optimistically update all list query caches
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey;
            if (key[0] !== 'invoices' || key.length < 1) return false;
            const secondKey = key[1];
            // Exclude detail queries (UUIDs), utility queries
            if (typeof secondKey === 'string' && secondKey.includes('-')) return false;
            if (secondKey === 'next-number' || secondKey === 'billing-queue') return false;
            return true;
          }
        },
        (old: any) => {
          if (!old?.invoices) return old;
          return {
            ...old,
            invoices: old.invoices.map((invoice: any) =>
              invoice_ids.includes(invoice.id)
                ? { ...invoice, invoice_date }
                : invoice
            ),
          };
        }
      );

      return { previousData };
    },

    onError: (err: any, _variables, context) => {
      // Rollback cache on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error(err?.message || 'Failed to update invoice dates');
    },

    onSuccess: (response) => {
      if (response.errors && response.errors.length > 0) {
        toast.warning(
          `Updated ${response.updated_count} invoice(s). ${response.errors.length} failed.`
        );
      } else {
        toast.success(`Updated ${response.updated_count} invoice date(s)`);
      }
    },

    onSettled: () => {
      // Re-sync with server after batch operation
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

// ============================================================================
// Phase 6: Void Payment Hook
// ============================================================================

/**
 * Void (delete) payment on an invoice
 *
 * Decrements amount_paid and automatically rolls back status from 'paid' to 'sent'
 * if amount_paid < total_amount after deletion.
 */
export function useVoidPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    // V172: updated from { amount } to { void_reason, payment_id? } to match new backend API
    mutationFn: ({ invoiceId, void_reason, payment_id }: { invoiceId: string; void_reason: string; payment_id?: string }) =>
      api.invoices.voidPayment(invoiceId, { void_reason, payment_id }),
    onSuccess: (data, variables) => {
      // Invalidate all invoice queries to reflect the payment deletion and potential status rollback
      // Phase 9.19e: Support both old ['invoices', id] and new ['invoices', id, 'full'] query keys
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId, 'full'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      // V172: also invalidate payment ledger rows
      queryClient.invalidateQueries({ queryKey: ['invoice', variables.invoiceId, 'payments'] });
      invalidateProjectListQueries(queryClient);
      toast.success('Payment voided successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to void payment');
    },
  });
}

// ============================================================================
// R4 (invoice-identity-cascade) — the inline cascade write hooks.
//
// These drive the InlineCascadePicker / AddDbaInline / LegalNameBlastRadius
// surfaces against R3's unified cascade contract. Each invalidates the invoice
// detail caches so the panel re-reads the saved state (AC4 — no green-over-
// stale; the panel value reflects the saved value before the confirmation
// renders, because the component patches/invalidates before showing it).
// ============================================================================

/** R4 — unified three-level cascade write (POST /{id}/cascade). */
export function useCascadeWrite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof api.invoices.cascadeWrite>[1];
    }) => api.invoices.cascadeWrite(id, data),
    // 2026-08-09: this hook had NO onError, so every failed cascade also drew
    // the global handler's generic "Your change was not saved — please try
    // again." (globalErrorHandlers.ts). That handler stays quiet only when it
    // can see `mutation.options.onError`, and the per-call `mutate(vars, {
    // onError })` form every BillToPanel cascade uses does not appear there —
    // it lives on the observer. So a failed hide-DBA save produced two
    // contradictory messages, and the generic one was false: the per-invoice
    // PUT had already landed.
    //
    // Defining it here claims ownership, and surfaces the SERVER's reason so a
    // call site that supplies no handler of its own still fails loudly rather
    // than silently. Callers that do supply one own the richer in-panel
    // banner; they no longer toast, so this stays the single toast.
    onError: (err: any) => {
      const reason = String(err?.message ?? '').trim();
      toast.error(
        reason ? `That didn't save: ${reason}` : "That didn't save. Please try again.",
        { duration: Infinity },
      );
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.id, 'full'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/** R4 — retroactive apply to unsent drafts (POST /{id}/cascade/retroactive). */
export function useCascadeRetroactive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof api.invoices.cascadeRetroactive>[1];
    }) => api.invoices.cascadeRetroactive(id, data),
    onSettled: (_data, _err, variables) => {
      // A retroactive apply touches sibling invoices — invalidate the list too.
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.id, 'full'] });
    },
  });
}

/** R4 — confirm-gated legal-name correction (POST /{id}/legal-name-correction). */
export function useLegalNameCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { new_legal_name: string; confirm: boolean };
    }) => api.invoices.legalNameCorrection(id, data),
    onSettled: (_data, _err, variables) => {
      // Only a confirmed write mutates; invalidate regardless (preview is cheap).
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.id, 'full'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/** R4 — easy in-context add-DBA (POST /{id}/add-dba). */
export function useAddDbaInContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof api.invoices.addDba>[1];
    }) => api.invoices.addDba(id, data),
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.id, 'full'] });
      // The org's establishment list changed.
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}
